import { Phone, X } from 'lucide-react';
import { useOverlayDrag } from './overlayDrag';
import { useDialer } from './dialer';

/**
 * The RingCentral softphone: one frame, mounted once by the module shell for
 * every tab — including the ones with no dialer in sight. That is the whole
 * point: RingCentral only invites a browser phone that was already registered
 * when the call arrived, so the frame has to be running before there is
 * anything to answer. It is never unmounted and never re-parented, because a
 * frame that stops rendering stops being a phone.
 *
 * It is a floating panel and nothing else, parked off screen with a transform
 * until a call or the agent asks for it — from the ring card, or from the
 * Calls tab's New call control. It used to have a second home docked in the
 * Calls column; that column is gone, so one phone now has one place.
 *
 * Nothing is drawn when the widget is the dashboard's (see useHostDialer): the
 * frame and its panel are the shell's, on screen on every dashboard page.
 *
 * The call itself — mute, hold, keypad, transfer, hang up — is the widget's
 * own UI. The console deliberately does not mirror those controls; a second
 * set would only drift from the ones the agent is actually looking at.
 */
export function DialerPanel() {
  const dialer = useDialer();
  const drag = useOverlayDrag();
  if (!dialer?.frameUrl) return null;
  const { attachFrame, frameUrl } = dialer;

  return (
    <div className={`rcdial${dialer.panelOpen ? ' open' : ''}`}>
      <div className="rcdial-head draggable" onPointerDown={drag.onPointerDown} title="Drag to move">
        <Phone size={14} />
        <span className="rcdial-title">Dialer</span>
        <span className="rcdial-state">{dialer.statusLine}</span>
        <button
          type="button"
          className="rcdial-close"
          title="Hide dialer — it keeps ringing"
          onClick={dialer.closePanel}
        >
          <X size={13} />
        </button>
      </div>
      <iframe
        ref={attachFrame}
        className="rcdial-frame"
        src={frameUrl}
        title="RingCentral dialer"
        allow="microphone; autoplay"
      />
    </div>
  );
}
